import { __ } from "@wordpress/i18n";
import { Fragment, useEffect, useState } from "@wordpress/element";
import { UpgradePro } from "../pages/upgrade-pro";
import { ImportDemo } from "../pages/import-demo";
import apiFetch from "@wordpress/api-fetch";
import classnames from "classnames";

const CloseButtonSVG = () => (
  <svg
    width="5"
    height="5"
    viewBox="0 0 5 5"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5 0.774648L4.22535 0L2.5 1.76056L0.774648 0L0 0.774648L1.76056 2.5L0 4.22535L0.774648 5L2.5 3.23944L4.22535 5L5 4.22535L3.23944 2.5L5 0.774648Z"
      fill="#99A2A9"
    />
  </svg>
);

const closeBannerAction = (setClose) => {
  const { bannerNonce } = window.jkitWizard || {};
  apiFetch({
    path: "jkit/v1/close-banner",
    method: "POST",
    data: {
      nonce: bannerNonce,
    },
  })
    .then(() => {
      setClose(true);
    })
    .catch(() => {
      setClose(true);
    });
};

const EventBanner = () => {
  const { bannerData } = window.jkitWizard || {};
  const [close, setClose] = useState(false);

  if (!bannerData || !bannerData.banner) return null;

  const today = new Date();
  const expired = new Date(bannerData?.expired);

  if (today > expired || close) return null;

  return (
    <div className="jkit-discount-banner">
      <a
        href={bannerData?.url + "&utm_medium=wizard-discount-banner"}
        target="_blank"
        rel="noreferrer"
      >
        <img src={bannerData?.banner} alt="event-banner" />
      </a>
      <button
        onClick={() => closeBannerAction(setClose)}
        type="button"
        className="jkit-close-banner"
      >
        <CloseButtonSVG />
      </button>
    </div>
  );
};

const getInstalledThemes = (func) => {
  apiFetch({
    path: "wp/v2/themes",
    method: "GET",
  }).then((data) => {
    func && func(data);
  });
};

const ImportLoading = (props) => {
  let progress = "0%";
  const width = () => {
    switch (props?.progress) {
      case "1/4":
        progress = "25%";
        return "twenty-five";
      case "2/4":
        progress = "50%";
        return "fifty";
      case "3/4":
        progress = "75%";
        return "seventy-five";
      case "4/4":
        progress = "100%";
        return "hundred";
      default:
        progress = "0%";
        return "zero";
    }
  };

  width();

  return (
    <div className="installing-notice">
      <div className="installing-notice-container">
        <div className="importing-notice">
          <div className="notice-inner">
            <span>{props?.message}</span>
            <span>{progress}</span>
          </div>
          <div className="bar-progress-container">
            <div className={"notice-bar-progress " + `${width()}-percent`} />
          </div>
        </div>
      </div>
    </div>
  );
};

const WizardFooter = (props) => {
  const { action, updateProgress, next, back, flag = null } = props;
  const { adminUrl } = window["jkitWizard"];

  switch (action) {
    case "loading":
      return (
        <Fragment>
          <ImportLoading
            message={installing?.message}
            progress={installing?.progress}
          />
        </Fragment>
      );
    default:
      return (
        <Fragment>
          <div
            className="button-skip"
            onClick={() => {
              window.location.href = adminUrl
                ? adminUrl
                : window.location.origin + "/wp-admin/";
            }}
          >
            <p>{__("Skip & Exit Setup", "jkit")}</p>
          </div>
          {flag !== "first-step" && (
            <div className="button-wrapper">
              <div
                onClick={() => updateProgress(back.progress, back.step)}
                className="button-back"
              >
                {__("Back", "jkit")}
              </div>
              <div
                onClick={() => updateProgress(next.progress, next.step)}
                className="button-next"
              >
                {__("Next", "jkit")}
              </div>
            </div>
          )}
        </Fragment>
      );
  }
};

const GettingStarted = ({ action, setAction, updateProgress, jkitWizard }) => {
  const { imageUrl } = jkitWizard;

  const contentBody = (
    <div className="content-body">
      <div
        className="icon-container"
        style={{
          backgroundImage: `url(${imageUrl + "/wizard-bg-lines-complete.png"})`,
        }}
      >
        <img className="icon-image" src={imageUrl + "/icon-jeg-kit.png"} />
      </div>
    </div>
  );

  return (
    <div className="theme-install">
      <div className="content-wrapper">
        {contentBody}
        <h1 className="content-title">
          {__("Let’s Get Started with Jeg Kit", "jkit")}
        </h1>
        <p className="content-desc">
          {__(
            "Thanks for installing Jeg Kit! We’ll guide you through a few quick steps to get everything ready.",
            "jkit",
          )}
        </p>
        <div className="button-wrapper">
          <div
            onClick={() => updateProgress("importDemo", 1)}
            className="button-next first-step"
          >
            {__("Get Started", "jkit")}
          </div>
        </div>
      </div>
      <div className="wizard-actions first-step">
        <WizardFooter
          flag="first-step"
          action={action}
          updateProgress={updateProgress}
          next={{ progress: "importDemo", step: 1 }}
        />
      </div>
    </div>
  );
};

const WizardPage = () => {
  const [progress, setProgress] = useState("gettingStarted");
  const [progressCount, setProgressCount] = useState(0);
  const [action, setAction] = useState("install");
  const jkitWizard = window.jkitWizard;

  const updateProgress = (progress, inc) => {
    setProgress(progress);
    setProgressCount(inc);
  };

  const content = () => {
    switch (progress) {
      case "done":
        const { imageUrl, adminUrl } = window["jkitWizard"];

        return (
          <div className="finalizing">
            <div className="image-wrapper">
              <img
                className="complete background"
                src={imageUrl + "/wizard-bg-lines-complete.png"}
              />
              <img
                className="complete icon"
                src={imageUrl + "/wizard-check-icon.png"}
              />
            </div>
            <div className="final-detail">
              <h3 className="final-title">
                {__("Your Setup Is Complete", "jkit")}
              </h3>
              <p className="final-desc">
                {__(
                  " Your plugin is ready to use! You can start building your site with the widgets and settings you’ve selected.",
                  "jkit",
                )}
              </p>
              <div
                onClick={() => {
                  window.location.href = adminUrl
                    ? adminUrl
                    : window.location.origin + "/wp-admin/";
                }}
                className="button-visit"
              >
                {__("Visit Dashboard", "jkit")}
              </div>
            </div>
          </div>
        );
      case "gettingStarted":
        return (
          <GettingStarted
            updateProgress={updateProgress}
            action={action}
            setAction={setAction}
            jkitWizard={jkitWizard}
          />
        );
      case "upgradePro":
        return (
          <UpgradePro
            updateProgress={updateProgress}
            WizardFooter={WizardFooter}
          />
        );
      case "importTemplate":
      default:
        return (
          <ImportDemo
            updateProgress={updateProgress}
            WizardFooter={WizardFooter}
          />
        );
    }
  };

  return (
    <div className="theme-wizard-wrapper">
      <div className="theme-wizard">
        <div className="wizard-header">
          <div
            className={`progress ${
              progress === "gettingStarted" ? "active" : ""
            } ${progressCount >= 1 ? "done" : ""}`}
          >
            <p className="number">
              {progressCount >= 1 ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.64662 16.0623L19.7089 5L21.1778 6.46884L8.64662 19L2.82031 13.1754L4.28915 11.7066L8.64662 16.0623Z"
                    fill="white"
                  />
                </svg>
              ) : (
                "01"
              )}
            </p>
            <h3 className="progress-title">{__("Getting Started", "jkit")}</h3>
          </div>
          <div className={`line ${progressCount >= 1 ? "done" : ""}`} />
          <div
            className={`progress ${progress === "importDemo" ? "active" : ""} ${
              progressCount >= 2 ? "done" : ""
            }`}
          >
            <p className="number">
              {progressCount >= 2 ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.64662 16.0623L19.7089 5L21.1778 6.46884L8.64662 19L2.82031 13.1754L4.28915 11.7066L8.64662 16.0623Z"
                    fill="white"
                  />
                </svg>
              ) : (
                "02"
              )}
            </p>
            <h3 className="progress-title">{__("Import Demo", "jkit")}</h3>
          </div>
          <div className={`line ${progressCount >= 2 ? "done" : ""}`} />
          <div
            className={`progress ${progress === "upgradePro" ? "active" : ""} ${
              progressCount >= 3 ? "done" : ""
            }`}
          >
            <p className="number">
              {progressCount >= 3 ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.64662 16.0623L19.7089 5L21.1778 6.46884L8.64662 19L2.82031 13.1754L4.28915 11.7066L8.64662 16.0623Z"
                    fill="white"
                  />
                </svg>
              ) : (
                "03"
              )}
            </p>
            <h3 className="progress-title">{__("Upgrade to Pro", "jkit")}</h3>
          </div>
          <div className={`line ${progressCount >= 3 ? "done" : ""}`} />
          <div
            className={`progress ${progress === "done" ? "done" : ""} ${
              progressCount >= 4 ? "done" : ""
            }`}
          >
            <p className="number">
              {progressCount >= 4 ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.64662 16.0623L19.7089 5L21.1778 6.46884L8.64662 19L2.82031 13.1754L4.28915 11.7066L8.64662 16.0623Z"
                    fill="white"
                  />
                </svg>
              ) : (
                "04"
              )}
            </p>
            <h3 className="progress-title">{__("Complete", "jkit")}</h3>
          </div>
        </div>
        <EventBanner />
        <div className="wizard-body">{content()}</div>
      </div>
    </div>
  );
};

export default WizardPage;
